/**
 * The V6 Capture features the V7 design dropped, brought back inside the V7 tabs (Andrew:
 * "add back and fill gaps"): clearinghouse batches under Readiness › At clearinghouse,
 * practitioner enrollment under Opportunity › Practitioner enrollment. Same layout as the
 * rest of the tab (gen.ts), from the same routes V6 read.
 */
import type { CapturePractitioner, CapturePractitioners, ClearinghouseBatch, ClearinghouseBatches } from '@aithora/shared';
import { dayYear, type Honesty } from './lib';
import type { GenData, GenUi } from './gen';

const RED = '#B3261E';
const GREEN = '#1F6B52';
const INK = '#22272E';
const MUTED = '#54606C';

const rejected = (b: ClearinghouseBatch) => b.ack277.rejected;
/** Why a batch bounced, in the clearinghouse's own words, counted. */
export function whyRejected(b: ClearinghouseBatch): string {
  if (!rejected(b)) return b.ack277.received < b.claims ? 'Awaiting 277CA acknowledgement' : 'All acknowledged';
  const counts = new Map<string, number>();
  for (const x of b.ack277.rejections) {
    const t = x.statusText ?? 'Category ' + x.category;
    counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts].map(([t, n]) => t + ' (' + n + ')').join(' · ');
}

/** Readiness › At clearinghouse: the batches that left, what was acknowledged and what bounced. */
export function withBatches(base: GenData, b: ClearinghouseBatches | null, ui: GenUi, act: { resend: (x: ClearinghouseBatch) => void }, network: boolean, tagSuffix: string): GenData {
  if ((ui.filter || 'All') !== 'At clearinghouse') return base;
  const all = b?.batches ?? [];
  const meas: Honesty | null = b ? (b.real ? 'MEASURED' : 'SAMPLE') : null;
  const id = (x: ClearinghouseBatch, i: number) => x.batchId ?? 'batch-' + i;
  const sel = all.find((x, i) => id(x, i) === ui.sel) ?? all[0] ?? null;
  const tot = all.reduce((a, x) => a + x.claims, 0);
  const acc = all.reduce((a, x) => a + x.claims - rejected(x), 0);
  const sent = (x: ClearinghouseBatch) => (x.submittedAt ? dayYear(x.submittedAt.slice(0, 10)) : '–');
  return {
    ...base,
    cols: 'minmax(0,1fr) 104px 58px 70px 70px minmax(0,1.5fr) 118px',
    minW: 660,
    heads: [['Batch', 'left'], ['Sent', 'left'], ['Claims', 'right'], ['Accepted', 'right'], ['Rejected', 'right'], ['Why rejected', 'left'], ['', 'right']],
    rows: all.map((x, i) => ({
      id: id(x, i),
      cells: [
        { t: x.batchId ?? '–', s: network ? x.facilityName ?? undefined : x.provider ?? undefined, w: '700' },
        { t: sent(x) },
        { t: String(x.claims), w: '700', right: true },
        { t: String(x.claims - rejected(x)), w: '700', c: GREEN, right: true },
        { t: String(rejected(x)), w: '700', c: rejected(x) ? RED : MUTED, right: true },
        { t: whyRejected(x) },
      ],
      btn: ui.done[id(x, i)] ? 'Resent ✓' : rejected(x) && x.batchId ? 'Fix and resend' : 'View',
      urgent: rejected(x) > 0,
    })),
    empty: b ? 'No batches have left yet. Claims go to the clearinghouse once the practitioner signs.' : '',
    rail: sel
      ? {
          kicker: 'CLEARINGHOUSE BATCH',
          title: sel.batchId ?? 'Batch',
          sub: [sel.provider, sel.environment, sent(sel)].filter(Boolean).join(' · '),
          why: rejected(sel) ? rejected(sel) + ' of ' + sel.claims + ' claims bounced before the payer saw them: ' + whyRejected(sel) + '.' : whyRejected(sel) + '.',
          whyBg: rejected(sel) ? 'rgba(179,38,30,.07)' : 'rgba(31,107,82,.08)',
          stats: [
            { k: 'First-pass acceptance', b: meas, v: tot ? ((acc / tot) * 100).toFixed(1) + '%' : '–', c: GREEN },
            { k: 'Rejected, fixable', b: meas, v: String(tot - acc), c: tot - acc ? RED : INK },
          ],
          listT: 'Acknowledgements',
          list: [
            { a: '999 · file accepted', b: sel.ack999?.verdict ?? 'not received yet', c: INK },
            { a: '277CA · claim status', b: sel.ack277.received + ' of ' + sel.claims + ' acknowledged', c: INK },
            ...sel.ack277.rejections.slice(0, 4).map((r) => ({ a: r.claimRef ?? 'Claim', b: r.statusText ?? 'Category ' + r.category, c: RED })),
          ],
          acts: rejected(sel) && sel.batchId ? [{ t: ui.done[id(sel, all.indexOf(sel))] ? 'Resent ✓' : 'Fix and resend', primary: true, go: () => act.resend(sel), write: true }] : [],
          foot: 'The enrolled practitioner submits every claim. Aithora never does.',
        }
      : null,
    foot: all.length + ' batches · 999 and 277CA acknowledgements' + tagSuffix,
  };
}

/** Practitioner enrollment state: -1 not enrolled, 0 partly, 1 enrolled everywhere. */
const stateOf = (p: CapturePractitioner) =>
  !p.credentialed ? -1 : p.medicaidStatus === 'pending' || p.medicareRevalidation === 'due_within_90_days' || p.medicaidRevalidation === 'due_within_90_days' ? 0 : 1;
const attention = (p: CapturePractitioner) => p.attention ?? p.problem ?? null;
export const PRAC_ACTIONS: Record<string, 'chase_enrollment' | 'start_revalidation' | 'start_enrollment'> = {
  'Chase enrollment': 'chase_enrollment',
  'Start revalidation': 'start_revalidation',
  'Start enrollment': 'start_enrollment',
};

/** Opportunity › Practitioner enrollment: who can bill, with which payers, and what needs doing. */
export function withPractitioners(base: GenData, pr: CapturePractitioners | null, ui: GenUi, act: { step: (p: CapturePractitioner) => void }, tagSuffix: string): GenData {
  const chips = [...base.chips, 'Practitioner enrollment'];
  if ((ui.filter || 'All') !== 'Practitioner enrollment') return { ...base, chips };
  const all = pr?.practitioners ?? [];
  const meas: Honesty | null = pr ? (pr.real ? 'MEASURED' : 'SAMPLE') : null;
  const sel = all.find((p) => p.id === ui.sel) ?? all[0] ?? null;
  const name = (p: CapturePractitioner) => (p.name ?? 'NPI ' + p.npi) + (p.credential ? ', ' + p.credential : '');
  const payers = (p: CapturePractitioner) => p.payers ?? [p.medicareEnrolled ? 'Medicare' : null, p.medicaidStatus ? 'Medicaid ' + p.medicaidStatus : null].filter(Boolean).join(' · ');
  const reval = (k: CapturePractitioner['medicareRevalidation'], due: string | null) =>
    k === 'overdue' ? 'Overdue' : k === 'due_within_90_days' ? 'Due ' + (due ? dayYear(due) : 'within 90 days') : due ? 'Due ' + dayYear(due) : 'No date on file';
  return {
    ...base,
    chips,
    cols: 'minmax(0,1.3fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.3fr) 56px 140px',
    minW: 660,
    note: 'The enrolled practitioner submits every claim · Aithora never does',
    heads: [['Practitioner', 'left'], ['Enrollment', 'left'], ['Covers', 'left'], ['Needs attention', 'left'], ['Claims', 'right'], ['', 'right']],
    rows: all.map((p) => {
      const st = stateOf(p);
      const att = attention(p);
      return {
        id: p.id,
        cells: [
          { t: name(p), s: p.specialty ?? 'NPI ' + p.npi, w: '700' },
          { t: st > 0 ? 'Enrolled' : st === 0 ? 'Partly enrolled' : 'Not enrolled', s: payers(p) || undefined, w: '700', c: st > 0 ? GREEN : RED },
          { t: (p.facilityNames ?? []).join(', ') || '–' },
          { t: att ?? 'Nothing', c: att ? RED : MUTED, w: att ? '700' : '500' },
          { t: p.claims != null ? String(p.claims) : '–', w: '700', right: true },
        ],
        btn: ui.done[p.id] ? 'Started ✓' : p.nextStep ?? 'View',
        urgent: st < 0,
      };
    }),
    empty: pr ? 'No practitioners on file for this building yet.' : '',
    rail: sel
      ? {
          kicker: 'PRACTITIONER',
          title: name(sel),
          sub: 'NPI ' + sel.npi + (sel.specialty ? ' · ' + sel.specialty : ''),
          why: attention(sel) ?? 'Enrolled with every payer they cover. Nothing to do.',
          whyBg: attention(sel) ? 'rgba(179,38,30,.07)' : 'rgba(31,107,82,.08)',
          stats: [
            { k: 'Medicare', b: meas, v: sel.medicareEnrolled ? 'Enrolled' : 'Not enrolled', c: sel.medicareEnrolled ? GREEN : RED },
            { k: 'Medicaid', b: meas, v: sel.medicaidStatus ? sel.medicaidStatus.charAt(0).toUpperCase() + sel.medicaidStatus.slice(1) : 'Not on file', c: sel.medicaidStatus === 'enrolled' ? GREEN : INK },
          ],
          listT: 'Revalidation',
          list: [
            { a: 'Medicare (PECOS)', b: reval(sel.medicareRevalidation, sel.medicareRevalidationDueOn), c: sel.medicareRevalidation === 'overdue' ? RED : INK },
            { a: 'Medicaid', b: reval(sel.medicaidRevalidation, sel.medicaidRevalidationDueOn), c: sel.medicaidRevalidation === 'overdue' ? RED : INK },
            { a: 'Billing-agent authorization', b: sel.billingAgentAuthorization ? 'On file' : 'Not on file', c: INK },
          ],
          acts: sel.nextStep && PRAC_ACTIONS[sel.nextStep] ? [{ t: ui.done[sel.id] ? 'Started ✓' : sel.nextStep, primary: true, go: () => act.step(sel), write: true }] : [],
          foot: 'Enrollment comes from PECOS and the state Medicaid file. Aithora chases it; the practitioner signs.',
        }
      : null,
    foot: all.length + ' practitioners' + tagSuffix,
  };
}
